import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { ApiEndpointDisplay } from './ApiEndpointDisplay';
import { ApiOverview } from './ApiOverview';
import sidebarApiReference from '../../data/sidebarApiReference.json';

// Import endpoint data
import registerData from '../../data/pages/menubuilder/register.json';
import uploadData from '../../data/pages/menubuilder/upload.json';
import getMenuData from '../../data/pages/menubuilder/getmenu.json';
import getStatusData from '../../data/pages/menubuilder/getstatus.json';
import upsertMenuItemData from '../../data/pages/menuitem/upsertmenu.json';
import deleteMenuItemData from '../../data/pages/menuitem/deletemenu.json';
import enrichMenuItemData from '../../data/pages/menuitem/enrichmenu.json';
import addModifierData from '../../data/pages/modifier/addmodifier.json';
import editModifierData from '../../data/pages/modifier/editmodifier.json';
import deleteModifierData from '../../data/pages/modifier/deletemodifier.json';
import getRestaurantsData from '../../data/pages/restaurants/getrestaurants.json';
import orderWebhookSettingsData from '../../data/pages/orders/orderwebhooksettings.json';
import createOrderData from '../../data/pages/orders/createorder.json';
import paymentStatusData from '../../data/pages/orders/paymentstatus.json';
import updateOrderData from '../../data/pages/orders/updateorder.json';
import merchantConfigData from '../../data/pages/merchant/merchantconfig.json';
import getVoiceConfigData from '../../data/pages/voice/getconfig.json';
import updateVoiceConfigData from '../../data/pages/voice/updateconfig.json';
import addVoiceConfigData from '../../data/pages/voice/addconfig.json';
import voiceConfigData from '../../data/pages/voice/config.json';
import getNumbersData from '../../data/pages/voice/getnumbers.json';
import numberAssignData from '../../data/pages/voice/numberassign.json';

interface DocsPageProps {
  sidebarData?: any;
}

export const DocsPage: React.FC<DocsPageProps> = ({ 
  sidebarData = sidebarApiReference
}) => {
  const { '*': wildcard } = useParams();
  const path = wildcard || '';
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const renderContent = () => {
    // Handle overview/introduction path
    if (!path || path === '' || path === 'introduction') {
      return <ApiOverview />;
    }

    // Handle specific endpoints
    switch (path) {
      case 'menubuilder/register':
        return <ApiEndpointDisplay data={registerData} />;
      case 'menubuilder/upload':
        return <ApiEndpointDisplay data={uploadData} />;
      case 'menubuilder/getmenu':
        return <ApiEndpointDisplay data={getMenuData} />;
      case 'menubuilder/getstatus':
        return <ApiEndpointDisplay data={getStatusData} />;
      case 'menuitem/upsertmenu':
        return <ApiEndpointDisplay data={upsertMenuItemData} />;
      case 'menuitem/deletemenu':
        return <ApiEndpointDisplay data={deleteMenuItemData} />;
      case 'menuitem/enrichmenu':
        return <ApiEndpointDisplay data={enrichMenuItemData} />;
      case 'modifier/addmodifier':
        return <ApiEndpointDisplay data={addModifierData} />;
      case 'modifier/editmodifier':
        return <ApiEndpointDisplay data={editModifierData} />;
      case 'modifier/deletemodifier':
        return <ApiEndpointDisplay data={deleteModifierData} />;
      case 'restaurants/getrestaurants':
        return <ApiEndpointDisplay data={getRestaurantsData} />;
      case 'orders/configurewebhook':
        return <ApiEndpointDisplay data={orderWebhookSettingsData} />;
      case 'orders/createorder':
        return <ApiEndpointDisplay data={createOrderData} />;
      case 'orders/paymentstatus':
        return <ApiEndpointDisplay data={paymentStatusData} />;
      case 'orders/updateorder':
        return <ApiEndpointDisplay data={updateOrderData} />;
      case 'merchant/merchantconfig':
        return <ApiEndpointDisplay data={merchantConfigData} />;
      case 'voice/getnumbers':
        return <ApiEndpointDisplay data={getNumbersData} />;
      case 'voice/numberassign':
        return <ApiEndpointDisplay data={numberAssignData} />;
      case 'voice/getconfig':
        return <ApiEndpointDisplay data={getVoiceConfigData} />;
      case 'voice/addconfig':
        return <ApiEndpointDisplay data={addVoiceConfigData} />;
      case 'voice/updateconfig':
        return <ApiEndpointDisplay data={updateVoiceConfigData} />;
      case 'voice/config':
        return <ApiEndpointDisplay data={voiceConfigData} />;
      default:
        // Redirect to Register Restaurants for unknown paths
        return <Navigate to="/api-reference/menubuilder/register" replace />;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Header />
      <div className="flex h-[calc(100vh-64px)] relative">
        {/* Sidebar with full height */}
        <div className={`relative transition-all duration-300 ${isSidebarOpen ? 'w-80' : 'w-0'}`}>
          <Sidebar 
            sections={sidebarData.sections} 
            isOpen={isSidebarOpen}
            onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          />
        </div>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-[var(--bg-primary)]">
          <div className="max-w-none px-8 py-8">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};
